import {MaterialSettingsTabs, MaterialSource, MaterialWithRecipes} from '@domain/materials'
import {Card} from '@hconnect/uikit/src/lib2'
import {useIsMutating} from '@tanstack/react-query'
import React, {useState, useCallback, useEffect, useMemo} from 'react'
import {FormProvider} from 'react-hook-form'
import {useSearchParams} from 'react-router'

import {HeaderTab, HeaderTabs} from '../../../../common/components/HeaderTabs'
import {MaterialDetailsCardHeader} from '../common/MaterialDetailsCardHeader'

import {ExistingMaterialDetailsCardContent} from './ExistingMaterialDetailsCardContent'
import {useMaterialForm, useMaterialFormSubmit, useExistingMaterialTabs} from './hooks'

interface MaterialDetailsCardProps {
  material: MaterialWithRecipes
}

export const ExistingMaterialDetailsCard: React.FC<MaterialDetailsCardProps> = ({material}) => {
  const [searchParams] = useSearchParams()

  // Preventing user access to invalid tabs while saving
  const runningMutations = useIsMutating({mutationKey: ['editMaterial']})
  const isMaterialSaving = Boolean(runningMutations)

  const materialForm = useMaterialForm(material)

  const availableTabs = useExistingMaterialTabs({
    materialWithRecipes: material,
    materialForm,
    isLoading: isMaterialSaving
  })

  const initialTab = useMemo(() => {
    const tabParam = searchParams.get('tab')
    if (tabParam === 'classification') {
      const classificationTab = availableTabs.find(
        (tab) => tab.value === (MaterialSettingsTabs.Classification as string)
      )
      return classificationTab || availableTabs[0]
    }
    return availableTabs[0]
  }, [searchParams, availableTabs])

  const [selectedTab, setSelectedTab] = useState<HeaderTab>(initialTab)

  const selectedTabValueRef = React.useRef(selectedTab.value)

  useEffect(() => {
    selectedTabValueRef.current = selectedTab.value
  }, [selectedTab])

  useEffect(() => {
    const matchingTab = availableTabs.find((tab) => tab.value === selectedTabValueRef.current)
    if (matchingTab) {
      setSelectedTab((prev) => {
        return prev !== matchingTab ? matchingTab : prev
      })
    } else {
      setSelectedTab(availableTabs[0])
    }
  }, [availableTabs])

  const onCancelClickHandler = useCallback(() => {
    const isMaterialOnlyBoughtByDefault =
      materialForm.formState.defaultValues?.source === MaterialSource.BoughtFromVendor

    const shouldTabBeHidden = [
      MaterialSettingsTabs.Recipes,
      MaterialSettingsTabs.PxTrendTags
    ].includes(selectedTab.value as MaterialSettingsTabs)

    if (isMaterialOnlyBoughtByDefault && shouldTabBeHidden) {
      setSelectedTab(availableTabs[0])
    }
    materialForm.reset()
  }, [materialForm, availableTabs, selectedTab.value])

  const handleSave = useMaterialFormSubmit(material, materialForm)

  const handleTabChange = useCallback(
    (tab: HeaderTab) => {
      setSelectedTab(tab)

      if (
        tab.value === (MaterialSettingsTabs.Classification as string) &&
        selectedTab.value === (MaterialSettingsTabs.General as string)
      ) {
        const dirtyFields = materialForm.formState.dirtyFields
        const hasGeneralFieldChanges =
          dirtyFields.name ||
          dirtyFields.description ||
          dirtyFields.source ||
          dirtyFields.globalMaterial

        if (
          materialForm.formState.isDirty &&
          hasGeneralFieldChanges &&
          materialForm.formState.isValid
        ) {
          void handleSave()
        }
      }
    },
    [materialForm, handleSave, selectedTab]
  )

  return (
    <Card
      headerContent={
        <MaterialDetailsCardHeader
          headerTitle={material.name}
          headerSubtitle={material.globalName}
          isCancelDisabled={isMaterialSaving || !materialForm.formState.isDirty}
          isSaveDisabled={isMaterialSaving || !materialForm.formState.isDirty}
          onCancelClick={onCancelClickHandler}
          onSaveClick={handleSave}
        />
      }
    >
      <HeaderTabs
        tabs={availableTabs}
        selectedTab={selectedTab}
        onTabChange={handleTabChange}
        testIdPrefix={'material-settings-card'}
        sxTabs={{mb: 3}}
        sxTab={{color: ({palette}) => palette.common.black, fontWeight: 500}}
      />
      <FormProvider {...materialForm}>
        <ExistingMaterialDetailsCardContent selectedMaterial={material} selectedTab={selectedTab} />
      </FormProvider>
    </Card>
  )
}
